import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function MeetingEditDialog({ meeting, user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [saving, setSaving] = useState(false);
  const fixedDepartment = user?.role === "member" && Boolean(user?.department);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setForm({ title: meeting.title, date: meeting.date, department: meeting.department });
    setDepartments([]);
    setLoadingDepartments(true);
    api.get("/departments", { signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted) setDepartments(data.preset);
      })
      .catch(() => {
        if (!controller.signal.aborted) toast.error("Gagal memuat daftar departemen. Buka kembali dialog untuk mencoba lagi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingDepartments(false);
      });
    return () => controller.abort();
  }, [open, meeting]);
  const submit = async (event) => {
    event.preventDefault(); setSaving(true);
    try {
      const { data } = await api.put(`/meetings/${meeting.id}`, { ...form, title: form.title.trim(), department: form.department.trim() });
      onSaved(data); setOpen(false); toast.success("Meeting diperbarui");
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return <Dialog open={open} onOpenChange={(value) => { if (!saving) setOpen(value); }}>
    <DialogTrigger asChild><Button variant="outline" className="h-9 w-full rounded-none px-3 shadow-none" data-testid="edit-meeting-btn"><Pencil size={16} /> Edit Meeting</Button></DialogTrigger>
    <DialogContent className="rounded-none">
      <DialogHeader><DialogTitle>Edit Meeting</DialogTitle><DialogDescription>Perbarui judul, tanggal, dan departemen meeting.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <label className="block text-sm">Judul<Input required value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
        <label className="block text-sm">Tanggal<Input type="date" required value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
        <div className="space-y-1.5">
          <label htmlFor="edit-meeting-department" className="block text-sm">Departemen</label>
          <Select value={form.department || ""} onValueChange={(value) => setForm({ ...form, department: value })} disabled={fixedDepartment || loadingDepartments || saving}>
            <SelectTrigger id="edit-meeting-department" data-testid="edit-meeting-department-select" className="rounded-none">
              <SelectValue placeholder={loadingDepartments ? "Memuat departemen..." : "Pilih departemen"} />
            </SelectTrigger>
            <SelectContent>
              {form.department && !departments.includes(form.department) && (
                <SelectItem value={form.department} disabled>{form.department} (saat ini)</SelectItem>
              )}
              {departments.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={saving || !form.title?.trim() || !form.department?.trim()}>{saving ? "Menyimpan..." : "Simpan Perubahan"}</Button>
      </form>
    </DialogContent>
  </Dialog>;
}
